import React, { useContext } from "react";
import { useTranslation } from "next-i18next";
import { useQueryClient } from "react-query";

// Styles
import Styles from "./Activation.module.scss";

// Components
import Search from "@vahak/core/dist/components/search/Search";
import AppBtn from "@vahak/core/dist/components/app-btn/AppBtn";
import { toast } from "@vahak/core/dist/components/toast/toast";
import SearchFullScreenMobile from "@vahak/core/dist/components/search-full-screen-mobile/SearchFullScreenMobile";

// Hooks
import { usePlaceSearch } from "@vahak/core/dist/hooks/useSearch";

// Context
import { AppContext } from "@vahak/core/dist/app-context";

// Assests
import Share from "@vahak/core/dist/icons/share_blue.svg";

// Types
import { CitySearchResultWithCateg } from "@vahak/core/dist/custom-types/location-search";
import { LoadMarketDetails, UserLoadInfoTypes } from "@vahak/core/dist/custom-types/load";
import { LorryMarketDetails, UserLorryInfoTypes } from "@vahak/core/dist/custom-types/lorry";
// Services
import { QueryNames } from "@vahak/core/dist/_services/hooks/queryNames";
import { useReactivateLorry } from "@vahak/core/dist/_services/hooks/usePostingService";
import { LocationSearchSource, searchUsingPlacesAPI } from "@vahak/core/dist/_services/city-search.service";

// Helpers
import { GA4EventNames } from "@vahak/core/dist/constants/event-names";
import { getLorryDetailsByType, LorryNameToNumberMap } from "@vahak/core/dist/constants/lorryTypes";
import { useToggleModalWithLocalState } from "@vahak/core/dist/hooks/useToggleModalWithLocalState";
import { useEventTrackerService } from "@vahak/core/dist/_services/hooks/useEventTrackerService";

interface ActivationProps {
    activateLorry: () => void;
    toggle: any;
    src?: CitySearchResultWithCateg;
    setSrc: React.Dispatch<React.SetStateAction<CitySearchResultWithCateg | undefined>>;
    data: (LoadMarketDetails & LorryMarketDetails) | any;
    lorryInfo?: UserLorryInfoTypes;
    setSearchingOnMobile?: React.Dispatch<React.SetStateAction<boolean>>;
    isSharingDisabled?: boolean;
}

const Activation = ({
    activateLorry,
    toggle,
    src,
    setSrc,
    data,
    lorryInfo,
    setSearchingOnMobile,
    isSharingDisabled
}: ActivationProps) => {
    const { t } = useTranslation("common");
    // Context
    const { recentTransporterCitySearches, companyId, isMobile, name } = useContext(AppContext);
    const { openModal, closeModal, isModalOpen } = useToggleModalWithLocalState(false);
    const getLastWord = (text: string) => {
        var n = text.split(" ");
        return n[n.length - 1];
    };

    //Events
    const { sendGAandMoEngageEvent } = useEventTrackerService();

    const { isSearchLoading, labelKey, onItemSelected, onSearchInput, searchOptions, searchText, onFocus } =
        usePlaceSearch({
            locationParamsOption: {
                source: LocationSearchSource.LorryPostingSrc
            },
            showCurrentLocationOption: true,
            defaultOptions: recentTransporterCitySearches,
            onSelectItem: (selected) => {
                if (selected && selected.length) {
                    /**
                     * Events
                     */

                    sendGAandMoEngageEvent({
                        name: GA4EventNames.LORRY_POSTING_MODAL.lorry_form_city_selected,
                        data: {
                            form_type: "reactivation",
                            vehicle_number: data.lorry_detail.number,
                            lorry_type: data.lorry_detail.type,
                            current_city: selected[0].name,
                            total_route: data.lorry_detail.routes,
                            vehicle_type: data.lorry_detail.lorry_type,
                            capacity: data.lorry_detail.capacity,
                            body_type: data.lorry_detail.body_type,
                            total_tyres: data.lorry_detail.total_tyres
                        }
                    });
                }

                if (isMobile) {
                    closeModal();
                    setSearchingOnMobile && setSearchingOnMobile(false);
                }
                setSrc(selected[0]);
            }
        });

    // Hooks
    const { mutateAsync: reactivateLorry } = useReactivateLorry();
    const queryClient = useQueryClient();

    const handleActivation = async () => {
        if (!src?.name) {
            toast.error("Select a city before proceeding");
            return;
        }

        /**
         * Events
         */

        sendGAandMoEngageEvent({
            name: GA4EventNames.LORRY_LISTING.lorry_form_reactivate_click,
            data: {
                form_type: "reactivation",
                vehicle_number: data.number,
                lorry_type: data.type,
                current_city: data.current_city,
                total_route: data.routes?.length,
                routes: data.routes,
                vehicle_type: getLorryDetailsByType(data.type)?.label,
                capacity: data.capacity,
                body_type: null,
                total_tyres: null,
                verification_document_type: null
            }
        });

        const currentPlaceID = (
            await searchUsingPlacesAPI(src?.name as string, {
                source: LocationSearchSource.LorryPostingSrc
            })
        )[0]?.place_id;
        reactivateLorry(
            { lorryId: data.lorry_id || data.lorry_detail.id || lorryInfo?.id, currentPlaceID },
            {
                onSuccess: (res) => {
                    toast.success("Lorry reactivated successfully.");
                    queryClient.invalidateQueries([
                        QueryNames.userLorries.getLorriesByCategory,
                        companyId,
                        // @ts-expect-error signature types
                        LorryNameToNumberMap[
                            data?.name || getLastWord(lorryInfo?.lorry_type_text || data?.lorry_detail.type)
                        ]
                    ]);
                    activateLorry();
                },
                onError: (res) => {
                    toast.error(res.message);
                }
            }
        );
    };

    const onClickCancel = () => {
        toggle();
    };

    return (
        <div className={Styles.activation}>
            {isMobile ? (
                <div
                    className={Styles.searchDiv}
                    onClick={() => {
                        openModal();
                        setSearchingOnMobile && setSearchingOnMobile(true);
                    }}
                >
                    {src ? src.name : t("share_modal_enter_vehicle_location")}
                </div>
            ) : (
                <>
                    <Search
                        isLoading={isSearchLoading}
                        id="src-search"
                        labelText=""
                        onSearch={onSearchInput}
                        onSelectItem={onItemSelected}
                        options={searchOptions}
                        labelKey={labelKey}
                        selectedItem={src}
                        searchText={searchText}
                        onFocus={onFocus}
                        placeholder={t("share_modal_enter_vehicle_location")}
                        minLength={0}
                        containerClassName={Styles.searchContainer}
                    />
                </>
            )}
            <div className={Styles.ctas}>
                {isMobile && !isSharingDisabled && (
                    <AppBtn className={Styles.cancel} fillType="outline" onClick={onClickCancel}>
                        {t("cancel_btn")}
                    </AppBtn>
                )}
                <AppBtn className={Styles.share} onClick={handleActivation} disabled={isSharingDisabled && !src}>
                    {isSharingDisabled ? (
                        t("re_activate_lorry")
                    ) : (
                        <>
                            <Share /> {t("share_btn")}
                        </>
                    )}
                </AppBtn>
            </div>
            <SearchFullScreenMobile
                isLoading={isSearchLoading}
                id="src-search"
                onSearch={onSearchInput}
                onSelectItem={onItemSelected}
                onFocus={onFocus}
                options={searchOptions}
                labelKey={labelKey}
                selectedItem={src}
                searchText={searchText}
                isVisible={!!isModalOpen}
                onClickBackBtn={() => {
                    setSearchingOnMobile && setSearchingOnMobile(false);
                    closeModal();
                }}
                placeholder={t("share_modal_enter_vehicle_location")}
            />
        </div>
    );
};

export default Activation;
